import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmService } from '../confirm';
import { ContextService } from '../context/context.service';
import { LoadingService } from '../loading/loading.service';
import { CountryModel } from '../user/country.model';
import { IStore, StoreModel } from '../user/store.model';
import { UserService } from '../user/user.service';
import { BrandService } from '../user/brand.service';
import { LocalizationPipe } from '../../shared/localization/localization.pipe';
import { LocalizationService } from '../localization/localization.service';

// RxJs
import { Subject } from 'rxjs';
import {catchError, filter, map, switchMap, takeUntil, tap} from 'rxjs/operators';

// ENVs
import { authConfig, environment } from 'environments/environment';

// Services
import { SecurityService } from '@app/core/security/service/security.service';


@Component({
  selector: 'pgs-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  providers: [LocalizationPipe]
})
export class NavbarComponent implements OnInit, OnDestroy {

  @ViewChild('storesModal', { static: true }) storesModal;

  private ngUnsubscribe: Subject<void>;

  activeClass = 'active';
  isCollapsed = true;
  permissionFor = {};
  userName: string;
  userCountry: string;
  userRole: string;
  tenants: CountryModel[];
  stores: IStore[];
  selectedTenant: CountryModel;
  selectedStore: IStore;
  brandIdNew: any;
  brand: any;
  items;
  isAuthenticating = false;
  isAuthenticated = false;
  currRoute: string;

  get currStore(): StoreModel {
    return this.contextService.store;
  }

  constructor(
    private userService: UserService,
    private securityService: SecurityService,
    private contextService: ContextService,
    private confirmService: ConfirmService,
    private modalService: NgbModal,
    private loadingService: LoadingService,
    private router: Router,
    private brandService: BrandService,
    private localizationPipe: LocalizationPipe,
    private localizationService: LocalizationService,
  ) { }

  async ngOnInit() {
    this.ngUnsubscribe = new Subject<void>();

    this.router.events.pipe(
      takeUntil(this.ngUnsubscribe),
      filter(e => e instanceof NavigationEnd)
    ).subscribe(
      (e: NavigationEnd) => this.currRoute = e.url
    );

    await this.userService.getUser().pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(user => {
      this.userCountry = user ? user.lstCountryStores[0].country : null;
      this.userRole = user ? user.lstRoles[0].identificationValue : null;
      this.userName = user ? user.name : null;
      if (user && user.lstTenantIds) {
        this.tenants = this.contextService.getTenancyModel(user.lstTenantIds);
        this.selectedTenant =
          this.tenants.find(t => t.shortLabel === this.contextService.country.toLocaleLowerCase());
      }

      if (user && user.stores && user.stores.length) {
        this.stores = user.stores.map(store => {

          const option = store.toIOption();

          if (this.contextService.country === 'PE') {
            this.brandService.getBrand(option.store.brandId).subscribe(data => {
              option.label = this.contextService.getStoreLabel(option) + data.name;
              option.disabled = option.value === this.currStore.identificationNumber;
            });
          }
          return option;
        });
      }
    });

    this.contextService.labesLoaded.pipe(
      filter(res => res),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(value => {
      this.isAuthenticating = false;
      this.isAuthenticated = true;
      this.buildMenu();
    });
  }

  ngOnDestroy(): void {
    if (this.ngUnsubscribe) {
      this.ngUnsubscribe.next();
      this.ngUnsubscribe.complete();
    }
  }
  

  public logout(): void {
    this.securityService.logOut();
  }


  public login(): void {
    this.securityService.logIn();
  }


  isCurrRoute(route: string): boolean {
    return this.currRoute && this.currRoute.includes(route + '/');
  }

  changeTenant(tenant: CountryModel) {
    this.confirmService.confirm(
      '¿Está seguro que desea cambiar el país?',
      'Al cambiar el país se perderán los cambios y será redireccionado a la página de inicio de PGS.',
      async r => {
        if (r) {
          this.loadingService.show('Cambiando país');
          this.router.navigate(['/']);
          await this.userService.changeCountry(tenant.shortLabel);
          this.contextService.changeCountry(tenant.shortLabel.toUpperCase(), true);
          this.selectedTenant = tenant;
        }
      },
      'btn-conversion'
    );
  }

  async showStoresModal(): Promise<void> {

    try {
      const r = await this.modalService.open(this.storesModal, {backdrop: 'static'}).result;
      if (r) {
        this.contextService.changeStore(this.selectedStore.store, true);
        this.selectedStore = undefined;
        this.stores = this.stores.map(store => {
          store.disabled = store.value === this.currStore.identificationNumber;
          return store;
        });
      }
    } catch {
    }
  }

  private buildMenu(): void {
    this.items = this.localizationPipe.transform('NAVBAR_ITEMS');
    if (!this.userService.buc) {
      const manteinerItems = this.items[0].subitems.filter(item => item.name !== 'Regularización datos BUC');
      this.items[0].subitems = manteinerItems;
    }
    
    const setItemVisibility = (item: any) => {
      const permission = this.userService.getPermissionsForModule(item.module);

      item['show'] = permission ? permission.action.read : false;
      if (this.contextService.country === 'PE' && this.userRole === 'FAC') {
        const manteinerProfile = this.items.find(i => i.name === 'Mantenedor');
        manteinerProfile.show = false;
      }

      if (this.userRole === 'C' && ['Ventas', 'Panel de seguimiento'].includes(item.name)) {
        item.show = false;
      }
      
      if (!item.subitems) {
        return;
      }
      for (const subitem of item.subitems) {
        setItemVisibility(subitem);
      }
    };

    for (const item of this.items) {
      setItemVisibility(item);
    }
  }
}
